import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Sidebar } from './components/layout/Sidebar';
import { AdminSidebar } from './components/layout/AdminSidebar';
import { Footer } from './components/layout/Footer';
import { ToastProvider } from './components/ui/Toast';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { CatalogPage } from './pages/Catalog';
import { LocationStep } from './pages/OfferWizard/Location';
import { CartStep } from './pages/OfferWizard/Cart';
import { CustomerInfoStep } from './pages/OfferWizard/CustomerInfo';
import { AuthorizedPersonStep } from './pages/OfferWizard/AuthorizedPerson';
import { OrderSummaryStep } from './pages/OfferWizard/OrderSummary';
import { OfferGeneratedPage } from './pages/OfferView/OfferGenerated';
import { SendOfferPage } from './pages/OfferView/SendOffer';
import { CustomerOfferView } from './pages/OfferView/CustomerOfferView';
import { ConfirmationPage } from './pages/CustomerFlow/Confirmation';
import { MyOffersPage } from './pages/MyOffers';
import { OfferDetailPage } from './pages/MyOffers/OfferDetail';
import { ReportsPage } from './pages/Reports';
import { ResellerManagementPage } from './pages/ResellerManagement';
import { NotificationsPage } from './pages/Notifications';
import { MyAccountPage } from './pages/MyAccount';
import { SettingsPage } from './pages/Settings';
import { HelpPage } from './pages/Help';
import { UserManagementPage } from './pages/UserManagement';
import { AdminDashboardPage } from './pages/Admin/Dashboard';
import { AdminOrdersPage } from './pages/Admin/Orders';
import { AdminResellersPage } from './pages/Admin/Resellers';
import { AdminProductsPage } from './pages/Admin/Products';
import { AdminFeedbackPage } from './pages/Admin/Feedback';
import './styles/globals.css';

const AuthenticatedLayout: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6c757d',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'salam-admin') {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </div>
        <Footer />
      </div>
    </div>
  );
};

const AdminRoute: React.FC = () => {
  const { user } = useAuth();
  if (user?.role !== 'reseller-admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
};

const SalamAdminLayout: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>Loading…</div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'salam-admin') return <Navigate to="/dashboard" replace />;
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AdminSidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </div>
        <Footer />
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <ToastProvider>
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/offer/:offerId" element={<CustomerOfferView />} />
        <Route path="/offer/confirmation" element={<ConfirmationPage />} />

        {/* Authenticated routes */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/offers/new" element={<Navigate to="/catalog" replace />} />

          {/* Offer wizard */}
          <Route path="/offers/wizard/location" element={<LocationStep />} />
          <Route path="/offers/wizard/cart" element={<CartStep />} />
          <Route path="/offers/wizard/customer" element={<CustomerInfoStep />} />
          <Route path="/offers/wizard/authorized-person" element={<AuthorizedPersonStep />} />
          <Route path="/offers/wizard/summary" element={<OrderSummaryStep />} />

          <Route path="/offers/:id/generated" element={<OfferGeneratedPage />} />
          <Route path="/offers/:id/send" element={<SendOfferPage />} />
          <Route path="/my-offers" element={<MyOffersPage />} />
          <Route path="/my-offers/:id" element={<OfferDetailPage />} />
          <Route path="/reports" element={<ReportsPage />} />

          {/* Utility pages */}
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/my-account" element={<MyAccountPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/help" element={<HelpPage />} />

          {/* Admin-only */}
          <Route element={<AdminRoute />}>
            <Route path="/reseller-management" element={<ResellerManagementPage />} />
            <Route path="/user-management" element={<UserManagementPage />} />
          </Route>
        </Route>

        {/* Salam Admin portal */}
        <Route element={<SalamAdminLayout />}>
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
          <Route path="/admin/resellers" element={<AdminResellersPage />} />
          <Route path="/admin/products" element={<AdminProductsPage />} />
          <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
  </AuthProvider>
);

export default App;
